import { useLingui } from '@lingui/react/macro'
import { Tooltip } from 'antd'
import { useChatbotSession } from 'features/chatbot/contexts/ChatbotSessionContext'
import { Clock, Copy, SquarePen } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import { ChatHistoryDrawer } from './ChatHistoryDrawer'
import { openTooltipStyle } from './conversationMessage/styles'
import { SessionTabs } from './SessionTabs'

const HEADER_BUTTON_HOVER = 'rgba(255, 255, 255, 0.1)'
const TOOLTIP_BG = '#1e1e1e'

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.5rem;
  position: relative;
`

const TabsArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const ActionsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${HEADER_BUTTON_HOVER};
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const CopyButton: React.FC = () => {
  const { t } = useLingui()
  const { activeSession } = useChatbotSession()

  const handleCopy = useCallback(() => {
    const text = activeSession.conversation
      .filter((msg) => msg.type !== 'thought')
      .map((msg) => {
        if (msg.type === 'userMessage') {
          const text = msg.text
          return t`User: ${text}`
        } else if (msg.type === 'aiResponse') {
          const content = msg.naturalLanguageResponseBySegments
            .map((s) => s.text)
            .join('\n')
          return t`Assistant: ${content}`
        } else if (msg.type === 'error') {
          const text = msg.text
          return t`Error: ${text}`
        }
        return ''
      })
      .filter(Boolean)
      .join('\n\n')

    navigator.clipboard.writeText(text).catch(() => {
      /* Clipboard access unavailable */
    })
  }, [activeSession.conversation, t])

  return (
    <Tooltip
      title={t`Copy`}
      placement='bottom'
      color={TOOLTIP_BG}
      arrow={false}
      styles={{ container: openTooltipStyle }}>
      <HeaderButton onClick={handleCopy} aria-label={t`Copy conversation`}>
        <Copy />
      </HeaderButton>
    </Tooltip>
  )
}

/**
 * Single-row header with session tabs on the left and action buttons on the right.
 * Sits above the data layer so it can access session context directly.
 */
export const SessionHeaderBar: React.FC = () => {
  const { t } = useLingui()
  const { createSession } = useChatbotSession()
  const [historyOpen, setHistoryOpen] = useState(false)

  return (
    <HeaderBar>
      <TabsArea>
        <SessionTabs />
      </TabsArea>
      <ActionsArea>
        <Tooltip
          title={t`History`}
          placement='bottom'
          color={TOOLTIP_BG}
          arrow={false}
          styles={{ container: openTooltipStyle }}>
          <HeaderButton
            onClick={() => setHistoryOpen((prev) => !prev)}
            aria-label={t`Chat history`}>
            <Clock />
          </HeaderButton>
        </Tooltip>
        <CopyButton />
        <Tooltip
          title={t`New Chat`}
          placement='bottom'
          color={TOOLTIP_BG}
          arrow={false}
          styles={{ container: openTooltipStyle }}>
          <HeaderButton
            onClick={() => createSession()}
            aria-label={t`New conversation`}>
            <SquarePen />
          </HeaderButton>
        </Tooltip>
      </ActionsArea>
      {historyOpen && (
        <ChatHistoryDrawer onClose={() => setHistoryOpen(false)} />
      )}
    </HeaderBar>
  )
}
